/* eslint-disable no-restricted-globals */
import { useCallback, useEffect, useRef } from 'react'
import { PlatformEvent } from 'lib/util'
import type { Attachment } from '@platform/react/hook/useAttachmentAPI'
import { ImageState } from 'ui/Element/Image'

export interface UseAttachmentClickProps {
  attachment?: Attachment | null
  attachmentState?: ImageState | null
  download?: boolean
  handler?: (event: PlatformEvent) => void
}

/**
 * Downloads `src` as a file called `name` by clicking a transient anchor
 * element.
 *
 * @param src  blob- or external url of the attachment
 * @param name name to download the attachment as
 */
const downloadFile = (src: string, name: string) => {
  const anchor = document.createElement('a')
  anchor.href = src
  anchor.download = name
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

/**
 * useAttachmentClick Hook
 *
 * Abstracts the logic when clicking an attachment for fetching and
 * downloading it through a transient anchor element.
 *
 * @param props            the hook's properties
 * @param props.attachment the attachment at hand
 * @returns                the click handler
 */
const useAttachmentClick = ({
  attachment,
  attachmentState,
  download = true,
  handler,
}: UseAttachmentClickProps) => {
  const { src = null, fetch } = attachmentState || {}
  const filename = (attachment?.value?.name || attachmentState?.name) as string

  // Set when the attachment is clicked before its source is available, so
  // that the download is triggered as soon as it has been fetched
  const pendingDownload = useRef(false)

  useEffect(() => {
    if (pendingDownload.current && attachmentState?.status === 'loaded' && src) {
      pendingDownload.current = false
      handler?.(new PlatformEvent('click', attachmentState))
      downloadFile(src, filename)
    }
  }, [attachmentState])

  const handleClick = useCallback((event?: unknown) => {
    if (!download) {
      !handler
        ? console.warn('Image has download prop set to false and no onClick handler. Clicking it has no effect.')
        : handler(new PlatformEvent(event, attachment))
    } else if (!src) {
      pendingDownload.current = true
      fetch?.()
    } else {
      downloadFile(src, filename)
      handler?.(new PlatformEvent(event, attachmentState))
    }
  }, [attachmentState, download, handler, attachment])

  return { onClick: handleClick }
}

export default useAttachmentClick
